/**
 * @file   src\Pages\FamilyFleetPlan.tsx
 * @brief  Family Fleet Plan Report page for each carwash.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Table,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  useState,
  Button,
  useParams,
  Pagination,
  React,
} from "../utils/thirdpartyComponents";
import { userType } from "../utils/enums";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, RevenueApi } from "../utils/apiUrls";
import SmallLoader from "../components/SmallLoader";
import { MessageToaster } from "../utils/toastUtil";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface FamilyFleetPlanRecord {
  TotalRows?: number;
  UserID?: number;
  ParentID?: string;
  FullName?: string;
  Phone?: string;
  IsParent?: number;
  MembersCount?: number;
  MemberWashCount?: number | null;
  LastVisitDate?: string | null;
  TotalWashCount?: number | null;
  TotalRevenue?: number | null;
  LicensePlateNumber?: string | null;
}

const FamilyFleetPlan = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;

  const navigate = useNavigate();

  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reportRecords, setReportRecords] = useState<FamilyFleetPlanRecord[]>([]);
  
  const toaster = new MessageToaster();
  const timezoneOffset = new Date().getTimezoneOffset();

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const pageSize = 10;

  // To display heading
  useEffect(() => {
    getCompany();
    getCompanyAddress();
  }, []);

  useEffect(() => {
    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    getFamilyFleetPlanReport(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Family Fleet Plan",
      url: carwash_owner
        ? ""
        : `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton:
        props.userType === userType["CARWASH-OWNER"] ? false : true,
      showMainMenu: true,
      activeLink:
        props.userType === userType["CARWASH-OWNER"]
          ? "reports"
          : "manage-car-wash",
    });
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "";
    try {
      return moment(dateString).format("MM/DD/YYYY");
    } catch (error) {
      return dateString;
    }
  };

  const groupByParentId = (records: FamilyFleetPlanRecord[]) => {
    const groups: Record<string, FamilyFleetPlanRecord[]> = {};

    records.forEach((record) => {
      const groupKey = record?.ParentID || String(record?.UserID ?? "");
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(record);
    });

    return Object.values(groups).map((group) => {
      const parent = group.find((item) => item?.IsParent === 1) || group[0];
      const children = group.filter((item) => item !== parent);
      const members = parent ? [parent, ...children] : children;
      const membersCount =
        parent?.MembersCount ?? Math.max(0, (members?.length || 0) - 1);
      const totalRevenue =
        parent?.TotalRevenue ??
        group.reduce((sum, item) => sum + (item?.TotalRevenue ?? 0), 0);

      return { parent, members, membersCount, totalRevenue };
    });
  };

  // Common function to fetch report data from API
  const fetchReportData = async (pageNo: number, pageSizeParam: number) => {
    const queryParams = new URLSearchParams();

    // pagination
    const offset = timezoneOffset;
    queryParams.append("offset", offset.toString());
    queryParams.append("pageNo", pageNo.toString());
    queryParams.append("pageSize", pageSizeParam.toString());

    // required carwash
    if (params.id) {
      queryParams.append("carWashId", params.id.toString());
    }

    const apiUrl = RevenueApi.CARWASH_OWNER_FAMILY_FLEET_PLAN;
    const url = `${apiUrl}?${queryParams.toString()}`;
    const { data } = await axios.get(url);

    if (data?.error === 0) {
      let records = [];
      let totalCount = 0;

      if (Array.isArray(data?.data)) {
        records = data.data;
        totalCount = data?.TotalRows || records.length;
      } else if (data?.data?.list && Array.isArray(data.data.list)) {
        records = data.data.list;
        totalCount = data.data.totalCount || data?.TotalRows || records.length;
      } else if (Array.isArray(data)) {
        records = data;
        totalCount = records.length;
      }

      const limit = data?.data?.limit || pageSizeParam || pageSize;

      return {
        list: records,
        totalCount: totalCount,
        limit: limit,
      };
    }

    return null;
  };

  const getFamilyFleetPlanReport = async (pageNo = 1) => {
    try {
      setLoading(true);
      const result = await fetchReportData(pageNo, pageSize);

      if (result) {
        if (result.list?.length > 0) {
          setReportRecords(result.list);
          setTotalPages(Math.ceil(result.totalCount / result.limit) || 1);
        } else {
          setReportRecords([]);
        }
      } else {
        setReportRecords([]);
      }
    } catch (error: any) {
      console.error("Error fetching family fleet plan report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to fetch report");
      setReportRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadReportAsExcel = async () => {
    try {
      setLoading(true);
      const result = await fetchReportData(1, 0); // pageNo = 1, pageSize = 0 to get all records

      if (result && result.list?.length > 0) {
        const groupedRecords = groupByParentId(result.list);
        const worksheetData: any[] = [];

        groupedRecords.forEach(({ parent, members, totalRevenue, membersCount }) => {
          if (members.length > 0) {
            members.forEach((member: FamilyFleetPlanRecord) => {
              worksheetData.push({
                "Parent Member Name": parent?.FullName ?? "",
                "No. of Family Members": membersCount,
                "Member Names": member?.FullName ?? "",
                "Member Phone Numbers": member?.Phone ?? "",
                "Vehicle Details": member?.LicensePlateNumber ?? "",
                "Total Visit Per Member": member?.MemberWashCount ?? 0,
                "Last Visit Date Per Member": formatDate(member?.LastVisitDate ?? undefined),
                "Total Washes": member?.TotalWashCount ?? 0,
                "Total Revenue($)": totalRevenue,
              });
            });
          } else {
            worksheetData.push({
              "Parent Member Name": parent?.FullName ?? "",
              "No. of Family Members": membersCount,
              "Member Names": "",
              "Member Phone Numbers": "",
              "Vehicle Details": "",
              "Total Visit Per Member": "",
              "Last Visit Date Per Member": "",
              "Total Washes": "",
              "Total Revenue($)": totalRevenue,
            });
          }
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "FamilyFleetPlan");

        const workbookArray = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([workbookArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileName = `FamilyFleetPlan_${moment().format("YYYY-MM-DD")}.xlsx`;
        saveAs(blob, fileName);
      } else {
        toaster.toastError("No records to download.");
      }
    } catch (error: any) {
      console.error("Error downloading family fleet plan report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to download report");
    } finally {
      setLoading(false);
    }
  };

  // Navigate according to url
  const itemClick = (url: string) => {
    navigate(url);
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Function to fetch company information
  const getCompany = async () => {
    try {
      const param1 = `${params.id}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="manage-carwash-container order-list-container multi-location-wrap">
      <Container>
        <Breadcrumb>
          {carwash_owner ? (
            <>
              <Breadcrumb.Item
                onClick={() => itemClick(`/${pageURLs.dashboard}`)}
              >
                Dashboard
              </Breadcrumb.Item>
            </>
          ) : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(
                    `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                  );
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}
          <Breadcrumb.Item active>Family Fleet Plan</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-end">
        <Button
            className="primary-btn"
            variant="outline-primary"
            title="Export Report"
            onClick={downloadReportAsExcel}
          >
            Excel Export
          </Button>
        </div>
        <div className="cw-table-wrap report-table">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>Parent Member Name</th>
                <th>No. of Family Members</th>
                <th>Member Names</th>
                <th>Member Phone Numbers</th>
                <th>Vehicle Details</th>
                <th>Total Visit Per Member</th>
                <th>Last Visit Date Per Member</th>
                <th>Total Washes</th>
                <th>Total Revenue($)</th>
              </tr>
            </thead>
            <tbody>
              {reportRecords.length > 0 ? (
                groupByParentId(reportRecords).map((group, index) => (
                  <React.Fragment key={index}>
                    {group.members.length > 0 ? (
                      group.members.map((member, memberIndex) => (
                        <tr key={`${index}-${memberIndex}`}>
                          <td>{memberIndex === 0 ? <strong>{group.parent?.FullName ?? ""}</strong> : ""}</td>
                          <td>{memberIndex === 0 ? group.membersCount : ""}</td>
                          <td>{member?.FullName ?? ""}</td>
                          <td>{member?.Phone ?? ""}</td>
                          <td>{member?.LicensePlateNumber ?? ""}</td>
                          <td>{member?.MemberWashCount ?? 0}</td>
                          <td>{formatDate(member?.LastVisitDate ?? undefined)}</td>
                          <td>{member?.TotalWashCount ?? 0}</td>
                          <td>
                            {memberIndex === 0
                              ? <strong>{group.totalRevenue ? `$${group.totalRevenue.toFixed(2)}` : "$0.00"}</strong>
                              : ""}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr key={index}>
                        <td>{group.parent?.FullName ?? ""}</td>
                        <td>{group.membersCount}</td>
                        <td colSpan={5} className="text-center">No family members</td>
                        <td>{group.totalRevenue ? `$${group.totalRevenue.toFixed(2)}` : "$0.00"}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center">
                    {loading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {reportRecords && reportRecords?.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      {loading && <SmallLoader />}
    </div>
  );
};

export default FamilyFleetPlan;

